// Périodes du rétroplanning : tranches de deux mois depuis la création jusqu'à
// J-2 mois, puis 2 mois, 1 mois, 2 semaines, 1 semaine avant et la veille.
// Bornes en mois avant le mariage (même découpage que le serveur).

export interface Period {
  key: string;
  from: number;
  to: number;
  kind: 'slice' | 'm2' | 'm1' | 'w2' | 'w1' | 'd1';
}

const DAYS_PER_MONTH = 30.4;

// Tranches de deux mois calées sur les mois pairs (…, 6-8, 4-6, 2-4) ; un reste
// de moins d'un mois au début est fusionné avec la première tranche.
export function sliceBounds(startMonths: number): Array<[number, number]> {
  const bounds: number[] = [];
  for (let b = 4; b < startMonths; b += 2) bounds.push(b);
  if (bounds.length && startMonths - bounds[bounds.length - 1] < 1) bounds.pop();
  const edges = [startMonths, ...bounds.reverse(), 2];
  const out: Array<[number, number]> = [];
  for (let i = 0; i < edges.length - 1; i++) if (edges[i] - edges[i + 1] >= 1) out.push([edges[i], edges[i + 1]]);
  return out;
}

export function buildPeriods(startMonths: number): Period[] {
  const periods: Period[] = [];
  sliceBounds(startMonths).forEach(([from, to]) => {
    periods.push({ key: `p${Math.round(from * 10) / 10}`, from, to, kind: 'slice' });
  });
  periods.push({ key: 'm2', from: 2, to: 1, kind: 'm2' }, { key: 'm1', from: 1, to: 0.5, kind: 'm1' });
  periods.push({ key: 'w2', from: 0.5, to: 0.25, kind: 'w2' }, { key: 'w1', from: 0.25, to: 0.05, kind: 'w1' });
  periods.push({ key: 'd1', from: 0.05, to: -1, kind: 'd1' });
  return periods;
}

export function periodOf(periods: Period[], due: number): Period {
  return periods.find((p) => due <= p.from && due > p.to) ?? (due > periods[0].from ? periods[0] : periods[periods.length - 1]);
}

// Échéance représentative d'une période (pour y déposer une tâche).
export const periodMid = (p: Period) => (p.kind === 'd1' ? 0.03 : (p.from + p.to) / 2);

const noon = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d, 12);
};

// Mois restants avant le mariage (négatif une fois passé).
export function monthsBefore(weddingDate: string, now = new Date()): number {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12);
  return (noon(weddingDate).getTime() - today.getTime()) / 86400000 / DAYS_PER_MONTH;
}

// Date située N mois avant le mariage.
export function dateBefore(weddingDate: string, months: number): Date {
  const d = noon(weddingDate);
  d.setDate(d.getDate() - Math.round(months * DAYS_PER_MONTH));
  return d;
}

// Échéance d'une tâche du catalogue : sa place dans le parcours étalée sur le
// temps disponible, bornée (ni trop tôt, ni trop tard) et jamais avant aujourd'hui.
export const dueFor = (months: number, pos: number, earliest: number, latest: number) =>
  Math.min(months, Math.max(latest, Math.min(earliest, months * (1 - pos))));
